import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductCard } from "@/components/ProductCard";
import { getProducts } from "@/lib/api";
import { HoundNote } from "@/components/HoundNote";

const TABS = [
  { slug: "all", label: "All", type: null },
  { slug: "founders", label: "Founders Collection", type: "collection" },
  { slug: "coming-soon", label: "Coming Soon", type: "category" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const active = params.get("collection") || params.get("category") || "all";

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    const query = {};
    if (params.get("collection")) query.collection = params.get("collection");
    if (params.get("category")) query.category = params.get("category");
    getProducts(query)
      .then((data) => {
        if (!cancelled) setProducts(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [params]);

  const selectTab = (tab) => {
    if (tab.type === null) setParams({});
    else setParams({ [tab.type]: tab.slug });
  };

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-28 -left-24 w-[28rem] h-[28rem] rounded-full bg-rosegold/20 blur-3xl" />
        <div className="absolute top-24 right-1/4 w-80 h-80 rounded-full bg-[#F3C6D1]/50 blur-3xl" />
        <div className="absolute top-1/2 -right-10 w-96 h-96 rounded-full bg-[#F3C6D1]/25 blur-3xl" />
        <div className="absolute -bottom-10 left-1/4 w-80 h-80 rounded-full bg-white/60 blur-3xl" />
      </div>
      <img
        src="/hound-logo.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute left-0 top-1/2 -translate-y-1/2 w-full opacity-[0.04] z-0"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">The collection</p>
        <h1 className="font-display text-4xl sm:text-5xl text-maroon">Shop Hound Oz</h1>
        <p className="mt-4 text-inkbrown/70 max-w-lg mx-auto">
          Cosy pieces for you and your hound, made to be lived in.
        </p>
      </div>

      <HoundNote
        author="George & Henry"
        message="We personally sniff-tested every single piece in here. Two very serious paws up from both of us."
        className="mx-auto mb-10 -rotate-1"
      />

      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12">
        {TABS.map((c) => (
          <button
            key={c.slug}
            onClick={() => selectTab(c)}
            data-testid={`filter-${c.slug}`}
            className={`px-5 py-2.5 rounded-full text-sm font-bubble transition-colors ${
              active === c.slug ? "bg-maroon text-cream" : "bg-white text-inkbrown/70 hover:text-maroon border border-secondary"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-inkbrown/50 py-20">Loading cosy things…</p>
      ) : products.length === 0 ? (
        <p className="text-center text-inkbrown/50 py-20">Nothing here just yet, check back soon.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8" data-testid="shop-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
      </div>
    </div>
  );
}
